import type { JSX } from 'react';
import clsx from 'clsx';
import { MdApartment, MdWork, MdLocationOn } from 'react-icons/md';
import { ImDatabase } from 'react-icons/im';
import type AgentApiAiJobSearchRunsResult from '@/types/serializers/AgentApiAiJobSearchRunsResult';
import { Button } from '@/components/Button';
import { truncateText } from '@/utils/truncateText';
import toRecommendationView, { type RecommendationView } from '../lib/toRecommendationView';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択中の推薦結果一覧（プレビュー表示に利用） */
  selectedResults: AgentApiAiJobSearchRunsResult[];
  /** 送信中フラグ */
  isSubmitting: boolean;
  /** 提案を確定する */
  onConfirm: () => void;
};

/**
 * AIおすすめ求人ダイアログから「求人提案する」を押した際に表示する確認モーダル
 * 1100px幅でカード状に求人サマリーを並べ、最終確認のうえ実際の提案処理を実行する
 */
export default function SuggestionConfirmDialog({
  open,
  onOpenChange,
  selectedResults,
  isSubmitting,
  onConfirm,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[1100px] max-w-[95vw] flex-col rounded-sm p-0">
        {/* ヘッダー */}
        <div className="flex h-14 shrink-0 items-center border-b border-solid border-gray-200 px-6">
          <DialogTitle className="text-base font-bold text-main">提案求人の確認</DialogTitle>
        </div>

        {/* ボディ */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
          <p className="text-base leading-[1.7] text-black">以下の求人を提案してよろしいですか？</p>

          {selectedResults.length === 0 ? (
            <p className="text-sm text-placeholder">選択中の求人はありません</p>
          ) : (
            <ul className="flex flex-col gap-4" aria-label="提案する求人一覧">
              {selectedResults.map((result) => {
                const view = toRecommendationView(result);
                return view ? (
                  <li key={result.id}>
                    <SuggestionJobCard jobPost={view} />
                  </li>
                ) : null;
              })}
            </ul>
          )}
        </div>

        {/* フッター */}
        <div className="flex h-[74px] shrink-0 items-center justify-end gap-2 border-t border-solid border-gray-200 px-6">
          <Button
            color="secondary"
            size="md"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || selectedResults.length === 0}
          >
            {isSubmitting ? '追加中...' : '提案求人を追加'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** 確認モーダル内の求人サマリーカード */
function SuggestionJobCard({ jobPost }: { jobPost: RecommendationView }) {
  const metaItems: { key: string; icon: JSX.Element; text: string; title?: string }[] = [];
  if (jobPost.companyName) {
    metaItems.push({
      key: 'company',
      icon: <MdApartment size={20} className="text-main" aria-hidden="true" />,
      text: jobPost.companyName,
    });
  }
  if (jobPost.jobPositionName) {
    metaItems.push({
      key: 'position',
      icon: <MdWork size={20} className="text-main" aria-hidden="true" />,
      // ポジション名は長くなりがちなので10文字程度で切り詰め、全文は title で表示
      text: truncateText(jobPost.jobPositionName),
      title: jobPost.jobPositionName,
    });
  }
  if (jobPost.salaryInfo) {
    metaItems.push({
      key: 'salary',
      icon: <ImDatabase size={18} className="text-main" aria-hidden="true" />,
      text: jobPost.salaryInfo,
    });
  }
  if (jobPost.workingLocation) {
    metaItems.push({
      key: 'location',
      icon: <MdLocationOn size={20} className="text-main" aria-hidden="true" />,
      text: jobPost.workingLocation,
    });
  }

  return (
    <div className="flex flex-col gap-3 rounded-sm border border-solid border-bright-gray bg-white p-6 shadow-[0_0_2px_rgba(0,0,0,0.2)]">
      <div className="flex items-center gap-2">
        {jobPost.source === 'shared' && (
          <span className="flex h-6 shrink-0 items-center rounded-sm bg-gray px-sm text-xs font-bold text-main">
            求人DB
          </span>
        )}
        <p className="truncate text-lg leading-normal font-bold text-main">{jobPost.title}</p>
      </div>

      {metaItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 text-xs text-main">
          {metaItems.map((item, index) => {
            const isLast = index === metaItems.length - 1;
            return (
              <span
                key={item.key}
                className={clsx(
                  'flex items-center gap-2',
                  !isLast && 'border-0 border-r border-solid border-border pr-4'
                )}
              >
                {item.icon}
                <span className="leading-[1.7]" title={item.title}>
                  {item.text}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
